import { Box, Typography, Paper, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Grade, Search } from '@mui/icons-material';

import { ColumnTitle, Pagination, ScrollbarCustom, WrapTable } from '../../../../components';
import { IUserDetail } from '../../../../types/User';
import { useGetList, useGetTotalByUser } from '../../../../services/rewards/useRewards';
import { convertToDate } from '../../../../utils/datetime';
import { useState } from 'react';
import RewardsStatus from '../../../../components/RewardsStatus';
import Copyable from '../../../../components/Copyable';

type TRewardPointRow = {
    _id: string;
    ampeco_session_id?: string;
    source_id?: string;
    reward_program_name?: string;
    reward_categories?: { name: string }[];
    value?: number;
    value_type?: string;
    reward_program?: {
        redeem_value?: number;
        redeem_by?: string;
    };
    reward_type_code: string;
    is_admin_assigned?: boolean;
    admin_name?: string;
    admin_email?: string;
    reward_vendor?: { name: string }[];
    source?: string;
    expiry_datetime?: string;
    status: string;
};


export const RewardPoints = ({ userDetail }: { userDetail: IUserDetail }) => {
    const [page, setPage] = useState(1);
    const { data: rewardData, isLoading: isLoadingRewardData, isFetching: isFetchingRewardData } = useGetList(page, { user_id: userDetail._id!, per_page: 10, include_vendor_and_category: true });
    const { data: rewardPointsData, isLoading: isLoadingRewardPointsData } = useGetTotalByUser(userDetail._id!);
    const { data: tableData, total, per_page } = rewardData || { data: [], total: 0, per_page: 10 };
    const { data: totalRewardPoints } = rewardPointsData || { data: { total_points: 0 } };

    const getSessionIdValue = (row: TRewardPointRow) => {
        return row.ampeco_session_id || row.source_id || '-';
    };

    const getProductReceivedValue = (row: TRewardPointRow) => {
        if (row.reward_program_name) {
            return row.reward_program_name;
        }

        if (row.reward_categories && row.reward_categories.length > 0) {
            return row.reward_categories.map((category) => category.name).join(', ');
        }

        return '-';
    };

    const getExpiryDateValue = (row: TRewardPointRow) => {
        return row.expiry_datetime ? convertToDate(row.expiry_datetime) : '-';
    };


    const formatSpentFieldValue = (
        redeemValue?: number,
        redeemBy?: string,
        rewardTypeCode?: string,
        isAdminAssigned?: boolean,
        adminName: string = "",
        adminEmail: string = ""
    ) => {
        if (!redeemValue) {
            return '-';
        }

        switch (rewardTypeCode) {
            case 'mystery_box':
                return 'Receive upon charging';
            default:
                if (isAdminAssigned) {
                    return `Complimentary by Admin ${adminName}${adminEmail ? ` (${adminEmail})` : ''}`
                }
                return `${redeemValue} ${redeemBy}${redeemValue > 1 ? 's' : ''}`;
        };
    }


    return (
        <Box sx={{ color: 'white', p: 4 }}>
            <Stack direction="row" alignItems="center" mb={4} spacing={2}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 32, mr: 1 }}>
                        <Grade style={{ fill: '#C891FF', width: 32, height: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>Reward Points</Typography>
                        <Typography variant="body2" color="gray">Get a detailed breakdown of user reward points.</Typography>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<Search />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Search
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Download CSV
                    </Button>
                    <IconButton sx={{ borderRadius: 3, color: 'white', border: '1px solid #444', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        <FilterListIcon />
                    </IconButton>
                </Stack>
            </Stack>
            <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4, mb: 4 }}>
                <Grid container spacing={4} mb={2} direction="row">
                    <Grid item xs={4}>
                        <Typography variant="body2" color="gray">Total Reward Points</Typography>
                        <Typography variant="h5" fontWeight={600}>{totalRewardPoints.available_points} pts</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="body2" color="gray">Status</Typography>
                        <Typography variant="h5" fontWeight={600}>Active</Typography>
                    </Grid>
                </Grid>
            </Paper>
            <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4 }}>
                <Box mb={2}>
                    <WrapTable p={"5px 21px"} height="100%" marginBottom={3} isLoading={isLoadingRewardData || isFetchingRewardData || isLoadingRewardPointsData}>
                        <TableContainer>
                            <ScrollbarCustom autoHide>
                                <Table sx={{ minWidth: 800, marginBottom: "10px" }}>
                                    <TableHead>
                                        <TableRow>
                                            <ColumnTitle align='center' sx={{ pl: 0 }}>SN</ColumnTitle>
                                            <ColumnTitle>Session ID</ColumnTitle>
                                            <ColumnTitle>Earned</ColumnTitle>
                                            <ColumnTitle>Spent</ColumnTitle>
                                            <ColumnTitle>Brand Partner</ColumnTitle>
                                            <ColumnTitle>Product Received</ColumnTitle>
                                            <ColumnTitle>Activities</ColumnTitle>
                                            <ColumnTitle>Expiry Date</ColumnTitle>
                                            <ColumnTitle>Status</ColumnTitle>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData.map((row: TRewardPointRow, index: number) => {
                                            const sessionIdValue = getSessionIdValue(row);
                                            const productReceivedValue = getProductReceivedValue(row);

                                            return (
                                                <TableRow key={row._id}>
                                                    <TableCell sx={{ pl: 0 }}>{(page - 1) * per_page + index + 1}</TableCell>
                                                    <TableCell>{sessionIdValue !== '-' ? <Copyable text={sessionIdValue} /> : '-'}</TableCell>
                                                    <TableCell>{row.value ? `${row.value} ${row.value_type}${row.value > 1 ? 's' : ''}` : '-'}</TableCell>
                                                    <TableCell>
                                                        {formatSpentFieldValue(
                                                            row?.reward_program?.redeem_value,
                                                            row.reward_program?.redeem_by,
                                                            row.reward_type_code,
                                                            row?.is_admin_assigned,
                                                            row?.admin_name,
                                                            row?.admin_email
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{
                                                        row.reward_vendor && row.reward_vendor.length > 0
                                                            ? row.reward_vendor.map((vendor) => vendor.name).join(', ')
                                                            : '-'
                                                    }</TableCell>
                                                    <TableCell>{productReceivedValue}</TableCell>
                                                    <TableCell>{row.source ? row.source : '-'}</TableCell>
                                                    <TableCell>{getExpiryDateValue(row)}</TableCell>
                                                    <TableCell align='center'>
                                                        <RewardsStatus status={row.status} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </ScrollbarCustom>
                        </TableContainer>
                    </WrapTable>
                    <Pagination
                        page={page}
                        setPage={setPage}
                        totalItems={total}
                        totalPage={Math.ceil(total / per_page)}
                        currentPage={page}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
